import { SurfaceType } from '../terrain/SurfaceType';

export interface SurfaceProperties {
  readonly type: SurfaceType;
  readonly friction: number;
  readonly rollingResistance: number;
  readonly landingTangentialRetention: number;
  readonly spinGrab: number;
  readonly restitution: number;
  readonly grassHeightMeters: number;
  readonly lieEffect: {
    readonly distanceMultiplier: number;
    readonly dispersionMultiplier: number;
    readonly spinMultiplier: number;
    readonly sidespinMultiplier: number;
    readonly launchAngleDeltaDegrees: number;
    readonly fixedDirectionalBiasDegrees: number;
    readonly rolloutResistanceMultiplier: number;
    readonly bounceRestitutionMultiplier: number;
  };
}

const SURFACE_PROPERTIES: Record<SurfaceType, SurfaceProperties> = {
  [SurfaceType.Tee]: {
    type: SurfaceType.Tee,
    friction: 0.72,
    rollingResistance: 1.6,
    restitution: 0.36,
    landingTangentialRetention: 0.42,
    spinGrab: 0.00006,
    grassHeightMeters: 0.01,
    lieEffect: {
      distanceMultiplier: 1,
      dispersionMultiplier: 1,
      spinMultiplier: 1,
      sidespinMultiplier: 1,
      launchAngleDeltaDegrees: 0,
      fixedDirectionalBiasDegrees: 0,
      rolloutResistanceMultiplier: 1,
      bounceRestitutionMultiplier: 1,
    },
  },
  [SurfaceType.Fairway]: {
    type: SurfaceType.Fairway,
    friction: 0.62,
    rollingResistance: 7.2,
    restitution: 0.32,
    landingTangentialRetention: 0.38,
    spinGrab: 0.00008,
    grassHeightMeters: 0.012,
    lieEffect: {
      distanceMultiplier: 1,
      dispersionMultiplier: 1,
      spinMultiplier: 1,
      sidespinMultiplier: 1,
      launchAngleDeltaDegrees: 0,
      fixedDirectionalBiasDegrees: 0,
      rolloutResistanceMultiplier: 1,
      bounceRestitutionMultiplier: 1,
    },
  },
  [SurfaceType.Rough]: {
    type: SurfaceType.Rough,
    friction: 0.82,
    rollingResistance: 9.5,
    restitution: 0.2,
    landingTangentialRetention: 0.24,
    spinGrab: 0.00014,
    grassHeightMeters: 0.07,
    lieEffect: {
      distanceMultiplier: 0.82,
      dispersionMultiplier: 1.65,
      spinMultiplier: 0.58,
      sidespinMultiplier: 1.35,
      launchAngleDeltaDegrees: -2.5,
      fixedDirectionalBiasDegrees: 2.5,
      rolloutResistanceMultiplier: 1.25,
      bounceRestitutionMultiplier: 0.8,
    },
  },
  [SurfaceType.Green]: {
    type: SurfaceType.Green,
    friction: 0.48,
    rollingResistance: 2.2,
    restitution: 0.26,
    landingTangentialRetention: 0.48,
    spinGrab: 0.00011,
    grassHeightMeters: 0.004,
    lieEffect: {
      distanceMultiplier: 0.92,
      dispersionMultiplier: 1.1,
      spinMultiplier: 0.85,
      sidespinMultiplier: 1,
      launchAngleDeltaDegrees: -1,
      fixedDirectionalBiasDegrees: 0,
      rolloutResistanceMultiplier: 1,
      bounceRestitutionMultiplier: 0.9,
    },
  },
  [SurfaceType.Bunker]: {
    type: SurfaceType.Bunker,
    friction: 1.15,
    rollingResistance: 18,
    restitution: 0.08,
    landingTangentialRetention: 0.12,
    spinGrab: 0.0002,
    grassHeightMeters: 0,
    lieEffect: {
      distanceMultiplier: 0.48,
      dispersionMultiplier: 2.4,
      spinMultiplier: 0.35,
      sidespinMultiplier: 1.55,
      launchAngleDeltaDegrees: 6,
      fixedDirectionalBiasDegrees: -5,
      rolloutResistanceMultiplier: 2.2,
      bounceRestitutionMultiplier: 0.45,
    },
  },
  [SurfaceType.Water]: {
    type: SurfaceType.Water,
    friction: 3,
    rollingResistance: 8,
    restitution: 0,
    landingTangentialRetention: 0,
    spinGrab: 0.0003,
    grassHeightMeters: 0,
    lieEffect: {
      distanceMultiplier: 0.25,
      dispersionMultiplier: 2.5,
      spinMultiplier: 0.2,
      sidespinMultiplier: 1,
      launchAngleDeltaDegrees: -8,
      fixedDirectionalBiasDegrees: 0,
      rolloutResistanceMultiplier: 3,
      bounceRestitutionMultiplier: 0,
    },
  },
  [SurfaceType.OutOfBounds]: {
    type: SurfaceType.OutOfBounds,
    friction: 1,
    rollingResistance: 3,
    restitution: 0.12,
    landingTangentialRetention: 0.25,
    spinGrab: 0.0001,
    grassHeightMeters: 0.1,
    lieEffect: {
      distanceMultiplier: 0.9,
      dispersionMultiplier: 1.25,
      spinMultiplier: 0.8,
      sidespinMultiplier: 1.15,
      launchAngleDeltaDegrees: 0,
      fixedDirectionalBiasDegrees: 0,
      rolloutResistanceMultiplier: 1.2,
      bounceRestitutionMultiplier: 0.8,
    },
  },
};

export function getSurfaceProperties(type: SurfaceType): SurfaceProperties {
  return SURFACE_PROPERTIES[type];
}
